import { cn } from '@/lib/utils'

import { addCalendarDays } from '@scrambl/shared'

import './engagement.css'

const DAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

export interface WeekStripProps {
  /** Monday, YYYY-MM-DD. */
  weekStart: string
  /** Org-local YYYY-MM-DD. */
  today: string
  activeDates: readonly string[]
  workingDays: readonly number[]
  atRiskToday?: boolean
  className?: string
}

/**
 * Mon–Sun dots: filled flame for active days, solid track for working days,
 * a dashed ring for days off (they never break a streak), and a soft pulse on
 * today while the streak is at risk.
 */
export function WeekStrip({
  weekStart,
  today,
  activeDates,
  workingDays,
  atRiskToday = false,
  className,
}: WeekStripProps) {
  const active = new Set(activeDates)
  const working = new Set(workingDays)
  const days = Array.from({ length: 7 }, (_, i) =>
    addCalendarDays(weekStart, i),
  )

  return (
    <ol
      className={cn('flex items-center gap-1.5', className)}
      aria-label="This week"
    >
      {days.map(day => {
        const weekday = new Date(`${day}T00:00:00Z`).getUTCDay()
        const isActive = active.has(day)
        const isToday = day === today
        const isFuture = day > today
        const isWorking = working.has(weekday)
        const state = isActive
          ? 'active'
          : isFuture
            ? 'upcoming'
            : isWorking
              ? isToday
                ? 'today'
                : 'missed'
              : 'off'
        return (
          <li
            key={day}
            className="flex flex-col items-center gap-1"
            aria-label={`${DAY_NAMES[weekday]}: ${
              state === 'active'
                ? 'active'
                : state === 'off'
                  ? 'day off'
                  : state === 'today'
                    ? 'not yet'
                    : state === 'upcoming'
                      ? 'upcoming'
                      : 'no activity'
            }`}
          >
            <span
              className={cn(
                'h-2.5 w-2.5 rounded-full',
                state === 'active' && 'bg-[hsl(var(--streak-flame))]',
                state === 'missed' && 'bg-foreground/15',
                state === 'upcoming' && isWorking && 'bg-foreground/[0.07]',
                (state === 'off' || (state === 'upcoming' && !isWorking)) &&
                  'border border-dashed border-foreground/25',
                state === 'today' &&
                  'border-2 border-[hsl(var(--streak-flame))]',
                state === 'today' && atRiskToday && 'streak-day-at-risk',
              )}
            />
            <span
              className={cn(
                'text-[10px] leading-none text-muted-foreground',
                isToday && 'font-semibold text-foreground',
              )}
              aria-hidden
            >
              {DAY_LETTERS[weekday]}
            </span>
          </li>
        )
      })}
    </ol>
  )
}
